import * as React from 'react';
import type { InertiaLinkProps } from '@inertiajs/react';
import { Link } from '@inertiajs/react';
import { cn } from '@/utils';
import useTypedPage from '@/Hooks/useTypedPage';
import { type forcedDomain } from '@/types';
import useForceUrlDomain from '@/Hooks/useForceUrl';

type BaseProps = {
  children: React.ReactNode;
  className?: string;
  href?: string;
  disabled?: boolean;
  styleType?: 'primary' | 'secondary' | 'tertiary' | 'link';
  proxy_enabled?: boolean;
  forceUrlDomain?: forcedDomain;
  busy?: boolean;
};

type ButtonAsButton = BaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
    as?: 'button';
  };

type ButtonAsUnstyled = Omit<ButtonAsButton, 'as' | 'styleType'> & {
  as: 'unstyled';
  styleType?: BaseProps['styleType'];
};

type ButtonAsLink = BaseProps &
  Omit<InertiaLinkProps, keyof BaseProps> & {
    as: 'link';
  };

type ButtonAsExternal = BaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
    as: 'externalLink';
  };

type ButtonAsInternal = BaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
    as: 'internalLink';
  };

export type ButtonProps =
  | ButtonAsButton
  | ButtonAsInternal
  | ButtonAsExternal
  | ButtonAsLink
  | ButtonAsUnstyled;

// eslint-disable-next-line react/display-name
const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>((props, ref) => {
  const {
    className,
    as,
    styleType,
    proxy_enabled,
    forceUrlDomain,
    busy,
    ...rest
  } = props;
  const { proxied } = useTypedPage<{ proxied: boolean }>().props;

  let allClassNames = cn(
    {
      'button group inline-flex items-center gap-3 rounded-lg  px-4 py-2 transition ease-in-out hover:bg-brand hover:dark:bg-brand-dark  disabled:dark:bg-brand-dark/80 hover:dark:text-brand-dark-75 font-medium font-sm whitespace-nowrap bg-brand-dark-100/20 text-brand-dark-75  disabled:text-brand-light-25 disabled:cursor-not-allowed hover:disabled:dark:bg-brand-dark-50 disabled:dark:text-brand-dark-50/40 hover:disabled:dark:text-brand-dark-25 focus:ring-1 focus:ring-brand focus:dark:ring-brand-dark text-base':
        styleType !== 'link',
      'bg-brand dark:bg-brand-dark text-brand-dark-100  hover:scale-105 transition hover:shadow-lg hover:ring-2 hover:ring-inset hover:ring-brand-light-25':
        styleType === 'primary',
      'dark:text-brand-light-75 dark:bg-brand-dark-25':
        styleType === 'secondary',
      'ring-1 ring-inset ring-brand-dark-100/20 dark:ring-brand-light-15 dark:text-brand-light-100 text-sm hover:dark:bg-brand-dark-50':
        styleType === 'tertiary',
      'is-busy': busy,
    },
    className
  );

  if (as === 'externalLink' || (proxied && props.href)) {
    rest.href = props.href || '';

    if (rest.href && proxied && props.forceUrlDomain) {
      rest.href = useForceUrlDomain(rest.href, props.forceUrlDomain);
    }

    return (
      <a
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        className={allClassNames}
        // provide good + secure defaults while still allowing them to be overwritten
        target={as === 'externalLink' ? '_blank' : '_self'}
        rel={as === 'externalLink' ? 'noopener noreferrer' : ''}
        {...rest}
      >
        {props.children}
      </a>
    );
  } else if (as === 'link' || props.href) {
    // don't pass unnecessary props to component
    return (
      <Link
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        className={allClassNames}
        {...rest}
      />
    );
  } else if (as === 'unstyled') {
    return (
      <button
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        className={className}
        {...rest}
      />
    );
  } else {
    return (
      <button
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        className={allClassNames}
        {...rest}
      />
    );
  }
});

export default Button;
